import { DOCUMENT } from '@angular/common';
import { ApplicationRef, ComponentRef, createComponent, EmbeddedViewRef, Inject, Injectable, Injector } from '@angular/core';
import { DomHandler } from 'primeng/dom';
import { DynamicDialogConfig, DynamicDialogInjector, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmDialogSeverity, NxConfirmDialogComponent } from './confirmDialogComponent';

export interface ConfirmDialogConfig {
    header?: string;
    message: string;
    severity?: ConfirmDialogSeverity;
    confirmLabel?: string;
    cancelLabel?: string;
}

@Injectable()
export class ConfirmDialogService {

    dialogComponentRefMap: Map<DynamicDialogRef, ComponentRef<NxConfirmDialogComponent>> = new Map();

    constructor(private appRef: ApplicationRef, private injector: Injector, @Inject(DOCUMENT) private document: Document) {}

    public open(confirmDialogConfig: ConfirmDialogConfig): DynamicDialogRef {
        let header = confirmDialogConfig.header;
        let config: DynamicDialogConfig = {
            header: header,
            width: '400px',
            contentStyle: { overflow: 'auto' }
        };
        return this.appendDialogComponentToBody(config, confirmDialogConfig);
    }

    private appendDialogComponentToBody(config: DynamicDialogConfig, confirmDialogConfig: ConfirmDialogConfig) {
        const map = new WeakMap();
        map.set(DynamicDialogConfig, config);

        const dialogRef = new DynamicDialogRef();
        map.set(DynamicDialogRef, dialogRef);

        const sub = dialogRef.onClose.subscribe(() => {
            this.dialogComponentRefMap.get(dialogRef).instance.close();
        });

        const destroySub = dialogRef.onDestroy.subscribe(() => {
            this.removeDialogComponentFromBody(dialogRef);
            destroySub.unsubscribe();
            sub.unsubscribe();
        });

        const componentRef = createComponent(NxConfirmDialogComponent, { environmentInjector: this.appRef.injector, elementInjector: new DynamicDialogInjector(this.injector, map) });
        (componentRef.instance as NxConfirmDialogComponent).message = confirmDialogConfig.message;
        (componentRef.instance as NxConfirmDialogComponent).severity = confirmDialogConfig.severity ? confirmDialogConfig.severity : ConfirmDialogSeverity.DEFAULT;
        (componentRef.instance as NxConfirmDialogComponent).cancelLabel = confirmDialogConfig.cancelLabel ? confirmDialogConfig.cancelLabel : 'coreModuleModule.confirmDialogService.cancelLabel';

        if(confirmDialogConfig.confirmLabel){
            (componentRef.instance as NxConfirmDialogComponent).confirmLabel = confirmDialogConfig.confirmLabel;
        } else if(confirmDialogConfig.header){
            (componentRef.instance as NxConfirmDialogComponent).confirmLabel = confirmDialogConfig.header;
        } else {
            (componentRef.instance as NxConfirmDialogComponent).confirmLabel = 'coreModuleModule.confirmDialogService.confirmLabel';
        }

        this.appRef.attachView(componentRef.hostView);

        const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
        if (!config.appendTo || config.appendTo === 'body') {
            this.document.body.appendChild(domElem);
        } else {
            DomHandler.appendChild(domElem, config.appendTo);
        }

        this.dialogComponentRefMap.set(dialogRef, componentRef);

        return dialogRef;
    }

    private removeDialogComponentFromBody(dialogRef: DynamicDialogRef) {
        if (!dialogRef || !this.dialogComponentRefMap.has(dialogRef)) {
            return;
        }

        const dialogComponentRef = this.dialogComponentRefMap.get(dialogRef);
        this.appRef.detachView(dialogComponentRef.hostView);
        dialogComponentRef.destroy();
        this.dialogComponentRefMap.delete(dialogRef);
    }

}
